import { useEffect, useState } from 'react';
import { getPushState, subscribeToPush, isStandalone } from '../utils/push';

// Session AH — the one-time "turn on notifications" prompt.
//
// ⚠️ Why this is a CARD WITH A BUTTON and not an automatic request: iOS only
// grants notification permission from a real user gesture, and treats an
// automatic request as a denial that the person then has to undo in Settings.
// The tap on Enable is what makes the request legal.
//
// ⚠️ Why it triggers on "running installed" rather than on install: no event
// fires when someone adds a PWA to the Home Screen on iOS — it cannot be
// hooked. The first launch INSIDE the installed app is the closest real moment,
// and it is the right one anyway: that is when push starts working.
//
// Dismissed once, dismissed for good (localStorage). The Account toggle is the
// permanent control — this is only the nudge.
const DISMISS_KEY = 'es_push_prompt_dismissed';

export default function PushPrompt() {
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (localStorage.getItem(DISMISS_KEY) === '1') return;
      if (!isStandalone()) return;            // browser tab — don't nag
      const state = await getPushState();
      // Only 'off' is worth prompting: 'on' is done, 'denied' cannot be undone
      // from here, and 'unsupported'/'needs-install' cannot succeed.
      if (!cancelled && state === 'off') setShow(true);
    })();
    return () => { cancelled = true; };
  }, []);

  if (!show) return null;

  const dismiss = () => {
    localStorage.setItem(DISMISS_KEY, '1');
    setShow(false);
  };

  const enable = async () => {
    setBusy(true); setError('');
    try {
      const r = await subscribeToPush();
      if (r.ok) { localStorage.setItem(DISMISS_KEY, '1'); setShow(false); return; }
      setError(
        r.reason === 'denied'
          ? 'Notifications are blocked. You can turn them on in your phone settings.'
          : 'Could not turn on notifications. You can try again from your Account page.'
      );
    } catch {
      setError('Could not turn on notifications. You can try again from your Account page.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={{
      position: 'fixed', left: 12, right: 12, bottom: 76, zIndex: 900,
      background: 'var(--bg-surface)', border: '1px solid var(--border)',
      borderRadius: 12, padding: '14px 16px',
      boxShadow: '0 6px 24px rgba(0,0,0,0.18)',
    }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
        <div style={{ fontSize: 22, lineHeight: 1 }}>🔔</div>
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: 700, fontSize: 14, color: 'var(--text-primary)', marginBottom: 4 }}>
            Turn on notifications
          </div>
          <div style={{ fontSize: 12, color: 'var(--text-secondary)', lineHeight: 1.5 }}>
            Hear about new shifts as soon as they're posted, without checking the app.
          </div>
          {error && <div style={{ fontSize: 12, color: 'var(--danger)', marginTop: 8 }}>{error}</div>}
          <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
            <button className="btn btn-primary" onClick={enable} disabled={busy}
              style={{ flex: 1, padding: '9px 14px', fontSize: 13 }}>
              {busy ? 'Turning on...' : 'Enable'}
            </button>
            <button onClick={dismiss}
              style={{ background: 'none', border: '1px solid var(--border)', borderRadius: 8,
                       padding: '9px 14px', fontSize: 13, color: 'var(--text-secondary)', cursor: 'pointer' }}>
              Not now
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
